import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Button } from '../../components/Common/Buttons';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';

type TextProp = string | ReactNode | undefined | null;

export enum ACTION_INTENT {
    SUCCESS = 'SUCCESS',
    WARNING = 'WARNING',
    DANGER = 'DANGER',
}

type MessageType = 'title' | 'message';

interface ConfirmationDialogOptions {
    title: TextProp;
    message: TextProp;
    id: string;
    keepMounted: boolean;
    onSuccessHandler: (value?: any) => void;
    onCancelHandler: (value?: any) => void;
    customFooter?: ReactNode;
    actionIntent: ACTION_INTENT;
}

export interface AskConfirmationOptions extends Partial<ConfirmationDialogOptions> {}

export type AskConfirmationFunc = (
    isOpen: boolean,
    options: Partial<ConfirmationDialogOptions>,
) => void;

export interface ConfirmationDialogContextInterface {
    askConfirmation: AskConfirmationFunc;
    dialogVisible: boolean;
}

export const ConfirmationDialogContext = createContext<ConfirmationDialogContextInterface>({
    askConfirmation: () => {},
    dialogVisible: false,
});

export const useConfirmation = () =>
    useContext<ConfirmationDialogContextInterface>(ConfirmationDialogContext);

export const ConfirmationDialogProvider: React.FC<React.PropsWithChildren<unknown>> = ({
    children,
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const { t } = useTranslation(['confirmation-dialog', 'buttons']);
    const [dialogVisible, setVisibility] = useState<boolean>(false);
    const theme = useTheme();
    const initialDialogOptions = {
        title: t('title'),
        message: t('message'),
        id: 'confirmation-dialog-from-context',
        keepMounted: false,
        onSuccessHandler: () => {},
        onCancelHandler: () => {},
        customFooter: undefined,
        actionIntent: ACTION_INTENT.SUCCESS,
    };
    const location = useLocation();
    const onClose = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setVisibility(false);
    };
    useEffect(() => {
        if (dialogVisible) onClose();
    }, [location]);

    const [dialogOptions, setDialogOptions] = useState<ConfirmationDialogOptions>({
        ...initialDialogOptions,
    });

    const askConfirmation: AskConfirmationFunc = (isOpen, options) => {
        setDialogOptions({
            ...dialogOptions,
            ...options,
        });
        setVisibility(isOpen);
    };

    const onCancelClick = async (e: React.MouseEvent) => {
        try {
            await dialogOptions.onCancelHandler();
        } catch (error) {
            throw error;
        } finally {
            onClose(e);
            // setDialogOptions({ ...initialDialogOptions });
        }
    };

    const onConfirmClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
        const target = event.target as HTMLButtonElement;

        try {
            setLoading(true);
            target.disabled = true;
            await dialogOptions.onSuccessHandler();
        } catch (error) {
            throw error;
        } finally {
            onClose();
            setLoading(false);
            target.disabled = false;
            setDialogOptions({ ...initialDialogOptions });
        }
    };

    const defineText = (text, type: MessageType) => {
        if (typeof text === 'string') {
            return <Typography variant={type === 'title' ? 'p18' : 'p14'}>{text}</Typography>;
        }

        if (text && typeof text !== 'string') {
            return text;
        }

        if (text === null) {
            return null;
        }

        return initialDialogOptions[type];
    };

    const defineButton = () => {
        switch (dialogOptions.actionIntent) {
            case ACTION_INTENT.WARNING:
                return {
                    text: t('buttons:continue_button_label'),
                    color: 'secondary',
                };
            case ACTION_INTENT.DANGER: {
                return {
                    text: t('buttons:delete_button_label'),
                    color: 'error',
                };
            }
            default:
                return {
                    text: t('buttons:confirm_button_label'),
                    color: 'primary',
                };
        }
    };

    return (
        <ConfirmationDialogContext.Provider
            value={{
                askConfirmation,
                dialogVisible,
            }}
        >
            {children}
            <Dialog
                open={dialogVisible}
                maxWidth="xs"
                fullWidth
                onClose={(e) => onClose(e as React.MouseEvent)}
                scroll="paper"
                PaperProps={{
                    sx: {
                        background: theme.palette.backgrounds.bg4,
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        width: '100%',
                        padding: '12px 16px',
                    }}
                >
                    <Typography variant="p20" fontWeight={700} color="text.primary">
                        {defineText(dialogOptions.title, 'title')}
                    </Typography>
                    <Typography variant="p16" color="text.primary" sx={{ margin: '16px 0' }}>
                        {defineText(dialogOptions.message, 'message')}
                    </Typography>
                </Box>
                <DialogActions sx={{ padding: '6px' }}>
                    {dialogOptions.customFooter ? (
                        dialogOptions.customFooter
                    ) : (
                        <>
                            <LoadingButton
                                loading={loading}
                                variant="contained"
                                sx={{ fontWeight: 500, padding: '4px 24px' }}
                                color={defineButton().color as any}
                                onClick={onConfirmClick}
                            >
                                {defineButton().text}
                            </LoadingButton>
                            <Button
                                variant="outlined"
                                color="info"
                                sx={{
                                    fontWeight: 500,
                                    padding: '4px 24px',
                                }}
                                onClick={onCancelClick}
                            >
                                {t('buttons:cancel_button_label')}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </ConfirmationDialogContext.Provider>
    );
};
